import { useEffect, useRef, useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import styles from './MapView.module.css'

interface MapViewProps {
  center?: [number, number]  // [lng, lat]
  zoom?: number
  onMove?: (center: [number, number], zoom: number) => void
  onClick?: (lngLat: [number, number]) => void
  onCenterChange?: (center: [number, number]) => void  // Called when map stops moving (drag end)
  showCenterPin?: boolean  // Show fixed pin at center
  className?: string
  children?: React.ReactNode
}

// Dark map style matching Tasamago design system
const DARK_MAP_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    'carto-dark': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors, © CARTO',
    },
  },
  layers: [
    {
      id: 'carto-dark',
      type: 'raster',
      source: 'carto-dark',
      minzoom: 0,
      maxzoom: 20,
    },
  ],
}

export function MapView({
  center = [106.8456, -6.2088],  // Jakarta default
  zoom = 12,
  onMove,
  onClick,
  onCenterChange,
  showCenterPin = false,
  className,
  children,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)

  const handleClick = useCallback((e: maplibregl.MapMouseEvent) => {
    onClick?.([e.lngLat.lng, e.lngLat.lat])
  }, [onClick])

  const handleMoveEnd = useCallback(() => {
    if (!mapRef.current) return
    const c = mapRef.current.getCenter()
    onMove?.([c.lng, c.lat], mapRef.current.getZoom())
    onCenterChange?.([c.lng, c.lat])
  }, [onMove, onCenterChange])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DARK_MAP_STYLE,
      center,
      zoom,
    })

    map.addControl(new maplibregl.NavigationControl(), 'top-right')

    if (onClick) {
      map.on('click', handleClick)
    }

    if (onMove || onCenterChange) {
      map.on('moveend', handleMoveEnd)
    }

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [center, zoom, handleClick, handleMoveEnd, onClick, onMove, onCenterChange])

  return (
    <div 
      ref={containerRef} 
      className={`${styles.map} ${className || ''}`}
      data-testid="map-container"
    >
      {showCenterPin && (
        <div className={styles.centerPin} aria-hidden="true">
          <svg width="36" height="48" viewBox="0 0 36 48" fill="none">
            <path d="M18 0C8.059 0 0 8.059 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.059 27.941 0 18 0z" fill="#FFC73C"/>
            <circle cx="18" cy="18" r="7" fill="#09090B"/>
            <circle cx="18" cy="18" r="3" fill="#FFC73C"/>
          </svg>
        </div>
      )}
      {children}
    </div>
  )
}
